/**
 * Channels service — Twilio + WhatsApp configuration.
 *
 * Backs Settings → Channels. Mirrors ai.spectraiq.backend.channel.controller
 * .ChannelSettingsController (/v1/channels/**).
 *
 * Response unwrapping: the backend wraps everything in ApiResponse<T>
 * ({success, data, message}), so each call peels one layer with the same
 * `r.data?.data ?? r.data` idiom the other services use.
 *
 * Note there is deliberately no `getCredentials()` — the backend never returns
 * a stored auth token, only a masked SID. If a dealer loses their token they
 * rotate it in the Twilio console.
 */
import { api } from '../api';

const unwrap = (r) => r.data?.data ?? r.data;

export default {
  /**
   * Everything the Channels panel renders, in one request: Twilio account
   * (masked), numbers, WhatsApp connections, and the webhook URLs. One call
   * rather than four because the panel polls it on a timer.
   */
  status: () => api.get('/v1/channels/status').then(unwrap),

  // ---- Twilio account ----------------------------------------------------

  getTwilio: () => api.get('/v1/channels/twilio').then(unwrap),

  /**
   * Save credentials. The backend verifies them against Twilio as part of the
   * save, so the resolved value already carries status ACTIVE or INVALID —
   * an INVALID result still comes back as HTTP 200 (the row was stored; Twilio
   * just didn't like it), so callers must check `status`, not just catch.
   */
  saveTwilio: (payload) => api.put('/v1/channels/twilio', payload).then(unwrap),

  testTwilio: () => api.post('/v1/channels/twilio/test').then(unwrap),

  disconnectTwilio: () => api.delete('/v1/channels/twilio').then(unwrap),

  // ---- Numbers -----------------------------------------------------------

  numbers: () => api.get('/v1/channels/numbers').then(unwrap),

  /** Pull the account's numbers from Twilio. Safe to re-run; won't clobber routing choices. */
  syncNumbers: () => api.post('/v1/channels/numbers/sync').then(unwrap),

  /** Patch one number's routing. Omitted fields are left alone server-side. */
  updateNumber: (id, patch) => api.put(`/v1/channels/numbers/${id}`, patch).then(unwrap),

  /** Point this number's Twilio webhooks at SpectraIQ (SMS + Voice, via the API). */
  provisionWebhooks: (id) => api.post(`/v1/channels/numbers/${id}/webhooks`).then(unwrap),

  /**
   * Remove SpectraIQ's webhooks from this number in Twilio — the inverse of
   * provision. The backend refuses to clear a webhook that isn't ours, so this
   * can't silently break an integration someone else set up on the number.
   */
  unprovisionWebhooks: (id) => api.delete(`/v1/channels/numbers/${id}/webhooks`).then(unwrap),

  /**
   * Mark a channel's webhook as configured by hand in Twilio.
   *
   * The only way to mark a WhatsApp sender as wired up — it has no
   * IncomingPhoneNumber for us to provision — and the correct path for any number
   * that lives in a Messaging Service, where the service's webhook overrides the
   * number's and auto-provisioning would write a URL Twilio simply ignores.
   *
   * @param channel 'SMS' | 'VOICE' | 'WHATSAPP'
   */
  markWebhookManual: (id, channel, manual, note = null) =>
    api.post(`/v1/channels/numbers/${id}/webhooks/manual`, { channel, manual, note })
       .then(unwrap),

  // ---- WhatsApp ----------------------------------------------------------
  //
  // A dealership runs WhatsApp through exactly ONE provider — QR pairing or a
  // Twilio sender, never both. WhatsApp is two-way: every inbound message gets an
  // AI reply, and that reply has to leave from the same WhatsApp identity the
  // customer messaged. So this is a switch, not a list.

  /** The dealership's WhatsApp connection. `{connected:false}` when none is chosen. */
  whatsapp: () => api.get('/v1/channels/whatsapp').then(unwrap),

  /**
   * Choose the WhatsApp provider. **Destructive** — this replaces the current
   * connection and tears the old transport down (a QR pairing is wiped and must
   * be re-scanned). Callers must confirm with the user first.
   *
   * @param provider 'QR' | 'TWILIO'
   * @param phoneNumber required for TWILIO — which synced number is the sender
   */
  chooseWhatsAppProvider: (provider, phoneNumber = null, label = null) =>
    api.put('/v1/channels/whatsapp/provider', { provider, phoneNumber, label }).then(unwrap),

  /** Remove WhatsApp entirely — stops sending and receiving. */
  disconnectWhatsApp: () => api.delete('/v1/channels/whatsapp').then(unwrap),

  // ---- Activity / monitoring ---------------------------------------------

  /**
   * Voice stream health check — the backend performs the same wss:// handshake
   * to voice-loop that Twilio does on an AI_AGENT call, and reports where it
   * got to (CONFIG/DNS/CONNECT/TLS/UPGRADE/TIMEOUT/OK) with an actionable hint.
   * Returns { ok, url, stage, latencyMs, error, hint }.
   */
  voiceHealth: () => api.post('/v1/channels/voice/health').then(unwrap),

  activity: (limit = 20) =>
    api.get('/v1/channels/activity', { params: { limit } }).then(unwrap),

  /**
   * Channel Monitor feed — every message and call, INCLUDING the ones the backend
   * refused (bad signature, wrong WhatsApp provider, unknown number).
   *
   * That's the point of it: Leads only show what succeeded, so a message Twilio
   * delivered and we rejected leaves no trace there — and Twilio reports our 403
   * back to the dealer as a vague "11200 HTTP retrieval failure".
   *
   * Returns { events, total, cap, problems }.
   */
  events: ({ limit = 200, channel = 'ALL', direction = 'ALL' } = {}) =>
    api.get('/v1/channels/events', { params: { limit, channel, direction } }).then(unwrap),
};
